// src/components/AsignacionesDrawer.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Stack,
  Typography,
  IconButton,
  Chip,
  Divider,
  Autocomplete,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import PersonAdd from "@mui/icons-material/PersonAdd";
import { useGuardiasAsignaciones } from "../../hooks/useGuardias";
import { useUsuarios } from "../../hooks/useUsuarios";
import type { Usuario } from "../../hooks/useUsuarios";

type Props = {
  open: boolean;
  onClose: () => void;
  turno: {
    id: number;
    capacidad: number;
  } | null;
};

/** Normaliza valores típicos que pueden venir de MySQL (TINYINT, strings, etc.) */
const isActiveTrue = (v: any) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (["1", "true", "si", "sí", "activo", "active", "enabled", "yes"].includes(s)) return true;
    if (["0", "false", "no", "inactivo", "inactive", "baja", "archivado", "disabled"].includes(s))
      return false;
  }
  return !!v;
};

export default function AsignacionesDrawer({ open, onClose, turno }: Props) {
  const turnoId = turno?.id;
  const capacidad = turno?.capacidad ?? 0;

  // ── ASIGNACIONES ─────────────────────────────────────────────
  const {
    rows,
    asignadosCount,
    refresh: refreshAsignaciones,
    add,
    liberar,
  } = useGuardiasAsignaciones(turnoId);

  // ── USUARIOS (lazy: requiere refresh explícito) ──────────────
  const {
    rows: usuarios,
    loading: loadingUsuarios,
    refresh: refreshUsuarios,
  } = useUsuarios({ sinClientes: true });

  // Filtro defensivo + excluir inactivos en el desplegable
  const usuariosAsignables = useMemo(
    () =>
      usuarios.filter((u) => String(u.rol).toLowerCase() !== "cliente" && isActiveTrue(u.activo)),
    [usuarios]
  );

  const [sel, setSel] = useState<Usuario | null>(null);

  useEffect(() => {
    if (!open) return;
    // Necesario para ver opciones en el Autocomplete
    refreshUsuarios();
    // Y cargar asignaciones del turno
    if (turnoId) refreshAsignaciones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, turnoId]);

  const asignados = rows.filter((r) => r.estado === "asignado");

  // Importante: map con TODOS los usuarios cargados
  // (aunque el Autocomplete solo use los "asignables")
  const usuariosMap = useMemo(() => new Map(usuarios.map((u) => [u.id, u])), [usuarios]);

  const capLlena = asignadosCount >= capacidad;

  const handleAdd = async () => {
    if (!sel || !turnoId || capLlena) return;
    await add(sel.id);
    setSel(null);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 420, p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Asignaciones</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
          <Chip label={`Capacidad: ${capacidad}`} />
          <Chip label={`Asignados: ${asignadosCount}`} color={capLlena ? "warning" : "default"} />
        </Stack>

        <Stack direction="row" spacing={1}>
          <Autocomplete
            sx={{ flex: 1 }}
            options={usuariosAsignables}
            getOptionLabel={(o) => o?.nombreCompleto || o?.email || String(o?.id)}
            value={sel}
            onChange={(_, v) => setSel(v)}
            isOptionEqualToValue={(a, b) => Number(a.id) === Number(b?.id)}
            filterSelectedOptions
            loading={loadingUsuarios}
            noOptionsText={loadingUsuarios ? "Cargando…" : "Sin usuarios disponibles"}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Añadir usuario"
                placeholder={capLlena ? "Turno completo" : "Busca por nombre o e‑mail"}
                InputProps={{
                  ...p.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsuarios ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                      {p.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={capLlena}
          />
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={handleAdd}
            disabled={!sel || !turnoId || capLlena}
          >
            Añadir
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Actuales
        </Typography>
        <List dense>
          {asignados.map((a) => {
            const u = usuariosMap.get(a.usuarioId);
            const label = u ? `${u.nombreCompleto ?? ""} · ${u.email}` : `Usuario #${a.usuarioId}`;
            return (
              <ListItem key={a.id} divider>
                <ListItemText primary={label} />
                <ListItemSecondaryAction>
                  <IconButton edge="end" onClick={() => liberar(a.id)} title="Liberar">
                    <DeleteOutline />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
          {asignados.length === 0 && (
            <ListItem>
              <ListItemText primary="Sin asignaciones" />
            </ListItem>
          )}
        </List>
      </Box>
    </Drawer>
  );
}
